import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { Users, Copy, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';

type Props = {
  hotelName: string;
};

type ChartData = {
  date: string;
  checkIn: number;
  checkOut: number;
  myCheckIn: number;
  myCheckOut: number;
  accommodated: number;
  myAccommodated: number;
};

type ChartStats = {
  totalAccommodated: number;
  myAccommodated: number;
  totalCheckIns: number;
  totalCheckOuts: number;
  myTotalCheckIns: number;
  myTotalCheckOuts: number;
  peakCheckInDate: string;
  peakCheckOutDate: string;
  peakCheckInCount: number;
  peakCheckOutCount: number;
};

export const CheckInOutChart = ({ hotelName }: Props) => {
  const [data, setData] = useState<ChartData[]>([]);
  const [stats, setStats] = useState<ChartStats>({
    totalAccommodated: 0,
    myAccommodated: 0,
    totalCheckIns: 0,
    totalCheckOuts: 0,
    myTotalCheckIns: 0,
    myTotalCheckOuts: 0,
    peakCheckInDate: '',
    peakCheckOutDate: '',
    peakCheckInCount: 0,
    peakCheckOutCount: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState<string>('quadruple');
  const { translations } = useLanguage();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Get all selector documents first
      const selectorRef = collection(firestore, 'PilgrimSelector');
      const allSelectorsSnapshot = await getDocs(selectorRef);

      // Get user's guides if organizer
      let userGuideIds: string[] = [];
      if (userRole === 'organizer') {
        const guidesRef = collection(firestore, 'Guides');
        const guidesQuery = query(guidesRef, where('organizerId', '==', userId));
        const guidesSnapshot = await getDocs(guidesQuery);
        userGuideIds = guidesSnapshot.docs.map(doc => doc.id);
      }

      // Process all check-in/check-out dates
      const dateMap = new Map<string, { 
        checkIn: number;
        checkOut: number;
        myCheckIn: number;
        myCheckOut: number;
        accommodated: number;
        myAccommodated: number;
      }>();
      
      let totalAccommodated = 0;
      let myAccommodated = 0;
      let totalCheckIns = 0;
      let totalCheckOuts = 0;
      let myTotalCheckIns = 0;
      let myTotalCheckOuts = 0;
      let peakCheckInCount = 0;
      let peakCheckOutCount = 0;
      let peakCheckInDate = '';
      let peakCheckOutDate = '';

      for (const doc of allSelectorsSnapshot.docs) {
        const selectorData = doc.data();
        const packageData = selectorData.packageData;

        // Check if pilgrim is accommodated in this hotel
        const isAccommodatedInHotel = selectorData.isAccommodated?.[hotelName] || false;
        
        // Determine if this is the user's pilgrim
        const isMyPilgrim = userRole === 'guide' ? 
          selectorData.guideId === userId :
          userRole === 'organizer' ? 
            userGuideIds.includes(selectorData.guideId) : 
            false;

        // Process each hotel type (main, shifting, madinah)
        const processHotel = (hotelData: any) => {
          if (hotelData?.hotel === hotelName) {
            // Filter by room type if selected
            if (selectedRoomType !== 'all' && hotelData.roomType !== selectedRoomType) {
              return;
            }

            const checkInDate = hotelData.startDate;
            const checkOutDate = hotelData.endDate;
            
            // Process check-in
            const currentCheckIn = dateMap.get(checkInDate) || { 
              checkIn: 0, 
              checkOut: 0,
              myCheckIn: 0,
              myCheckOut: 0,
              accommodated: 0,
              myAccommodated: 0
            };

            currentCheckIn.checkIn++;
            if (isMyPilgrim) {
              currentCheckIn.myCheckIn++;
              myTotalCheckIns++;
            }
            if (isAccommodatedInHotel) {
              currentCheckIn.accommodated++;
              totalAccommodated++;
              if (isMyPilgrim) {
                currentCheckIn.myAccommodated++;
                myAccommodated++;
              }
            }
            totalCheckIns++;
            dateMap.set(checkInDate, currentCheckIn);

            if (currentCheckIn.checkIn > peakCheckInCount) {
              peakCheckInCount = currentCheckIn.checkIn;
              peakCheckInDate = checkInDate;
            }
            
            // Process check-out
            const currentCheckOut = dateMap.get(checkOutDate) || { 
              checkIn: 0, 
              checkOut: 0,
              myCheckIn: 0,
              myCheckOut: 0,
              accommodated: 0,
              myAccommodated: 0
            };
            currentCheckOut.checkOut++;
            if (isMyPilgrim) {
              currentCheckOut.myCheckOut++;
              myTotalCheckOuts++;
            }
            totalCheckOuts++;
            dateMap.set(checkOutDate, currentCheckOut);

            if (currentCheckOut.checkOut > peakCheckOutCount) {
              peakCheckOutCount = currentCheckOut.checkOut;
              peakCheckOutDate = checkOutDate;
            }
          }
        };

        processHotel(packageData.mainMakkah);
        processHotel(packageData.shiftingMakkah);
        processHotel(packageData.madinah);
      }

      // Convert map to array and sort by date
      const sortedData = Array.from(dateMap.entries())
        .map(([date, counts]) => ({
          date,
          checkIn: counts.checkIn,
          checkOut: counts.checkOut,
          myCheckIn: counts.myCheckIn,
          myCheckOut: counts.myCheckOut,
          accommodated: counts.accommodated,
          myAccommodated: counts.myAccommodated
        }))
        .sort((a, b) => {
          const [aDay, aMonth, aYear] = a.date.split('/').map(Number);
          const [bDay, bMonth, bYear] = b.date.split('/').map(Number);
          return new Date(aYear, aMonth - 1, aDay).getTime() - 
                 new Date(bYear, bMonth - 1, bDay).getTime();
        });

      setData(sortedData);
      setStats({
        totalAccommodated,
        myAccommodated,
        totalCheckIns,
        totalCheckOuts,
        myTotalCheckIns,
        myTotalCheckOuts,
        peakCheckInDate,
        peakCheckOutDate,
        peakCheckInCount,
        peakCheckOutCount
      });
    } catch (error) {
      console.error('Error fetching check-in/out data:', error);
      toast.error(translations?.accommodationChart?.loadError || 'Failed to load data');
    } finally {
      setIsLoading(false);
      setIsDataLoaded(true);
    }
  };

  useEffect(() => {
    if (isDataLoaded) {
      fetchData();
    }
  }, [selectedRoomType]);

  if (isLoading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {translations?.accommodationChart?.loading || 'Loading data...'}
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Users className="text-primary-500" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              {translations?.accommodationChart?.checkInOutTitle || 'Check-in and Check-out Schedule'}
            </h3>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Load Data Button */}
          {!isDataLoaded && (
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Users size={20} />
              <span>{translations?.accommodationChart?.loadData || 'Load Data'}</span>
            </button>
          )}

          {/* Export Button */}
          <button
            onClick={() => {
              try {
                // Prepare data for export
                const exportData = data.map(item => ({
                  'Date': item.date,
                  'Check-in': item.checkIn,
                  'My Check-in': item.myCheckIn,
                  'Check-out': item.checkOut,
                  'My Check-out': item.myCheckOut,
                  'Accommodated': item.accommodated,
                  'My Accommodated': item.myAccommodated
                }));

                // Add summary stats
                exportData.push(
                  {},
                  {
                    'Date': 'Summary Statistics',
                    'Check-in': stats.totalCheckIns,
                    'My Check-in': stats.myTotalCheckIns,
                    'Check-out': stats.totalCheckOuts,
                    'My Check-out': stats.myTotalCheckOuts,
                    'Accommodated': stats.totalAccommodated,
                    'My Accommodated': stats.myAccommodated
                  }
                );

                const ws = XLSX.utils.json_to_sheet(exportData);

                // Set column widths
                ws['!cols'] = [
                  { wch: 12 }, // Date
                  { wch: 10 }, // Check-in
                  { wch: 12 }, // My Check-in
                  { wch: 10 }, // Check-out
                  { wch: 12 }, // My Check-out
                  { wch: 12 }, // Accommodated
                  { wch: 15 }, // My Accommodated
                ];

                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Check-in Data');

                // Generate filename with hotel name and date
                const date = new Date().toISOString().split('T')[0];
                const filename = `${hotelName}-checkin-data-${date}.xlsx`;

                // Save file
                XLSX.writeFile(wb, filename);

                toast.success(translations?.accommodationChart?.exportSuccess || 'Data exported successfully');
              } catch (error) {
                console.error('Error exporting data:', error);
                toast.error(translations?.accommodationChart?.exportError || 'Failed to export data');
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            <Download size={20} />
            <span>{translations?.accommodationChart?.export || 'Export Data'}</span>
          </button>

          {/* Room Type Filter */}
          <select
            value={selectedRoomType}
            onChange={(e) => setSelectedRoomType(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            disabled={isLoading}
          >
            <option value="all">{translations?.roomTypesChart?.all || 'All Room Types'}</option>
            <option value="double">{translations?.roomTypesChart?.double || 'Double'}</option>
            <option value="triple">{translations?.roomTypesChart?.triple || 'Triple'}</option>
            <option value="quadruple">{translations?.roomTypesChart?.quadruple || 'Quadruple'}</option>
          </select>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Check-ins */}
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="text-green-500" size={20} />
            <span className="font-medium text-green-700">
              {translations?.accommodationChart?.totalCheckIns || 'Total Check-ins'}
            </span>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {stats.totalCheckIns}
            {userRole !== 'admin' && (
              <span className="text-sm font-normal ml-2">
                ({stats.myTotalCheckIns} {translations?.accommodationChart?.yours || 'yours'})
              </span>
            )}
          </div>
          <div className="text-sm text-green-600 mt-1">
            {translations?.accommodationChart?.peakOn || 'Peak on'} {stats.peakCheckInDate}
          </div>
        </div>

        {/* Total Check-outs */}
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="text-red-500" size={20} />
            <span className="font-medium text-red-700">
              {translations?.accommodationChart?.totalCheckOuts || 'Total Check-outs'}
            </span>
          </div>
          <div className="text-2xl font-bold text-red-600">
            {stats.totalCheckOuts}
            {userRole !== 'admin' && (
              <span className="text-sm font-normal ml-2">
                ({stats.myTotalCheckOuts} {translations?.accommodationChart?.yours || 'yours'})
              </span>
            )}
          </div>
          <div className="text-sm text-red-600 mt-1">
            {translations?.accommodationChart?.peakOn || 'Peak on'} {stats.peakCheckOutDate}
          </div>
        </div>

        {/* Accommodated */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="text-blue-500" size={20} />
            <span className="font-medium text-blue-700">
              {translations?.accommodationChart?.accommodated || 'Accommodated'}
            </span>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {stats.totalAccommodated}
          </div>
          <div className="text-sm text-blue-600 mt-1">
            {translations?.accommodationChart?.totalAccommodated || 'Total accommodated pilgrims'}
          </div>
        </div>

        {/* My Accommodated */}
        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Users className="text-purple-500" size={20} />
            <span className="font-medium text-purple-700">
              {translations?.accommodationChart?.myAccommodated || 'My Accommodated'}
            </span>
          </div>
          <div className="text-2xl font-bold text-purple-600">
            {stats.myAccommodated}
          </div>
          <div className="text-sm text-purple-600 mt-1">
            {translations?.accommodationChart?.myTotalAccommodated || 'My accommodated pilgrims'}
          </div>
        </div>
      </div>

      {/* Chart */}
      {data.length > 0 ? (
        <div className="w-full max-w-[700px] aspect-[2.5/1.5] overflow-hidden mx-auto">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
                tick={{ fontSize: 12 }}
              />
              <YAxis />
              <Tooltip 
                formatter={(value: number, name: string) => {
                  switch (name) {
                    case 'checkIn':
                      return [value, translations?.accommodationChart?.checkIn || 'Check-in'];
                    case 'checkOut':
                      return [value, translations?.accommodationChart?.checkOut || 'Check-out'];
                    case 'myCheckIn':
                      return [value, translations?.accommodationChart?.myCheckIn || 'My Check-in'];
                    case 'myCheckOut':
                      return [value, translations?.accommodationChart?.myCheckOut || 'My Check-out'];
                    case 'accommodated':
                      return [value, translations?.accommodationChart?.accommodated || 'Accommodated'];
                    case 'myAccommodated':
                      return [value, translations?.accommodationChart?.myAccommodated || 'My Accommodated'];
                    default:
                      return [value, name];
                  }
                }}
              />
              <Legend />
              <Bar
                dataKey="checkIn"
                name={translations?.accommodationChart?.checkIn || 'Check-in'}
                fill="#047857"
                stackId="a"
              />
              <Bar
                dataKey="myCheckIn"
                name={translations?.accommodationChart?.myCheckIn || 'My Check-in'}
                fill="#10B981"
                stackId="a"
              />
              <Bar
                dataKey="checkOut"
                name={translations?.accommodationChart?.checkOut || 'Check-out'}
                fill="#991B1B"
                stackId="a"
              />
              <Bar
                dataKey="myCheckOut"
                name={translations?.accommodationChart?.myCheckOut || 'My Check-out'}
                fill="#EF4444"
                stackId="a"
              />
              <Bar
                dataKey="accommodated"
                name={translations?.accommodationChart?.accommodated || 'Accommodated'}
                fill="#1D4ED8"
                stackId="b"
              />
              {userRole !== 'admin' && (
                <Bar
                  dataKey="myAccommodated"
                  name={translations?.accommodationChart?.myAccommodated || 'My Accommodated'}
                  fill="#3B82F6"
                  stackId="b"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {isDataLoaded ? (
            translations?.accommodationChart?.noData || 'No data available'
          ) : (
            translations?.accommodationChart?.clickToLoad || 'Click Load Data to view statistics'
          )}
        </div>
      )}
    </div>
  );
};
